<script lang="ts">
  import { onMount } from 'svelte';
  import TracedBoard from '../game/TracedBoard.svelte';
  import WordChip from '../ui/WordChip.svelte';
  import { tracedTiles } from '../game/board-trace';
  import { Clock } from '../platform/clock.svelte';
  import { powerUpTileStyle } from '../game/chaos';
  import { pointsForWord } from '../../game/rules';
  import { POWER_UP_INFO } from '../content/power-ups';
  import type { TutorialArt } from '../content/tutorial';
  import type { PowerUpKind } from '../../shared/power-ups';

  /**
   * A tutorial page's illustration, drawn from the live board, chip and power-up presentation so
   * every theme pack styles it without separate artwork. Decorative: the page text carries the rules.
   */
  interface Props {
    art: TutorialArt;
    powerUps?: readonly PowerUpKind[] | undefined;
    /** Motion allowed: the trace draws tile by tile on a loop; otherwise it shows complete. */
    animate: boolean;
  }
  let { art, powerUps = [], animate }: Props = $props();

  const MINI_BOARD_SIZE = 3;
  /** Traces S-T-A-R, turning a diagonal from A to R. */
  const TRACE_LETTERS = ['s', 't', 'a', 'e', 'r', 'o', 'n', 'c', 'k'];
  const TRACE_PATH = [0, 1, 2, 4];
  /** Traces G-O-L-D through the power-up waiting on D. */
  const POWER_UP_LETTERS = ['g', 'o', 'l', 'a', 'd', 'e', 'm', 's', 't'];
  const POWER_UP_PATH = [0, 1, 2, 4];
  /** The line runs on through this tile unselected, since selection would hide its treatment. */
  const POWER_UP_TILE = 4;
  const SCORED_WORDS = ['cat', 'star', 'stare', 'starts'];
  /** The clock face shows a round with roughly a third of its time left. */
  const CLOCK_LABEL = '0:32';
  const CLOCK_REMAINING = 0.35;
  const CLOCK_RADIUS = 42;
  const CLOCK_CIRCUMFERENCE = 2 * Math.PI * CLOCK_RADIUS;
  /** One showing of the word, traced then held by the same rule as the post-match recap. */
  const TRACE_LOOP_MS = 2_000;

  const tracing = $derived(art === 'trace' || art === 'power-up');
  const tracePath = $derived(art === 'power-up' ? POWER_UP_PATH : TRACE_PATH);
  // The shared clock pauses while the page is hidden, like CSS animations.
  const clock = new Clock('frame');
  const startedAt = Date.now();
  onMount(() => clock.mount());
  $effect(() => clock.run(animate && tracing));
  const shownPath = $derived(
    animate ? tracedTiles(tracePath, ((clock.now - startedAt) % TRACE_LOOP_MS) / TRACE_LOOP_MS) : tracePath,
  );
</script>

<div class="art" aria-hidden="true">
  {#if tracing}
    <TracedBoard
      board={art === 'trace' ? TRACE_LETTERS : POWER_UP_LETTERS}
      size={MINI_BOARD_SIZE}
      traced={shownPath}
      powerUp={art === 'power-up' ? { tile: POWER_UP_TILE, kind: 'gold' } : undefined}
    />
  {:else if art === 'score'}
    <div class="chips">
      {#each SCORED_WORDS as word (word)}<WordChip {word} points={pointsForWord(word)} />{/each}
    </div>
  {:else if art === 'clock'}
    <svg class="clock" viewBox="0 0 100 100">
      <circle class="clock-track" cx="50" cy="50" r={CLOCK_RADIUS} />
      <circle
        class="clock-remaining"
        cx="50"
        cy="50"
        r={CLOCK_RADIUS}
        stroke-dasharray={CLOCK_CIRCUMFERENCE}
        stroke-dashoffset={CLOCK_CIRCUMFERENCE * (1 - CLOCK_REMAINING)}
      />
      <text x="50" y="50">{CLOCK_LABEL}</text>
    </svg>
  {:else}
    <div class="power-ups">
      {#each powerUps as kind (kind)}
        <figure>
          <span class="token" style={powerUpTileStyle(kind)}></span>
          <figcaption>{POWER_UP_INFO[kind].title}</figcaption>
        </figure>
      {/each}
    </div>
  {/if}
</div>

<style>
  .art {
    height: 148px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .chips {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  .clock {
    width: 136px;
    height: 136px;
  }
  .clock circle {
    fill: none;
    stroke-width: 8;
  }
  .clock-track {
    stroke: var(--theme-muted-text);
    opacity: 0.25;
  }
  .clock-remaining {
    stroke: var(--theme-accent);
    stroke-linecap: round;
    transform: rotate(-90deg);
    transform-origin: center;
  }
  .clock text {
    fill: currentColor;
    font-family: var(--theme-board-font);
    font-size: 24px;
    font-weight: 700;
    text-anchor: middle;
    dominant-baseline: central;
  }
  .power-ups {
    display: flex;
    justify-content: center;
    gap: 14px;
  }
  figure {
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  /* A power-up tile in the pack's treatment, with its glyph drawn large rather than in a corner. */
  .token {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--theme-tile-radius);
    background: var(--pu-background);
    background-size: var(--pu-background-size);
    border: var(--pu-border);
    box-shadow: var(--pu-shadow);
    transform: var(--pu-transform);
    animation: var(--pu-animation);
  }
  .token::before {
    content: var(--pu-glyph);
    color: var(--pu-glyph-color);
    font-size: 24px;
    line-height: 1;
  }
  figcaption {
    font-size: 12px;
    font-weight: 700;
  }
</style>
